import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { iAluno } from "../../types/iAluno";
import { iEIL } from "../../types/iEIL";

interface iAlunoTabFinanceiro {
    registro?: iAluno;
}

function formatDateBR(value?: string | null): string {
    if (!value) return "-";
    const datePart = String(value).substring(0, 10);
    const parts = datePart.split("-");
    if (parts.length !== 3) return String(value);
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function formatBRL(value: any): string {
    if (value === null || value === undefined || value === "") return "-";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function sortInstallments(installments: any[]): any[] {
    return [...installments].sort((a, b) => {
        const na = Number(a?.NumberInEnrollment) || 0;
        const nb = Number(b?.NumberInEnrollment) || 0;
        if (na !== nb) return na - nb;
        const da = a?.DueDate ? new Date(a.DueDate).getTime() : 0;
        const db = b?.DueDate ? new Date(b.DueDate).getTime() : 0;
        return da - db;
    });
}

function enrollmentRecency(enr: any): number {
    const date = enr?.Date || enr?.dhDate;
    if (date) {
        const ts = new Date(date).getTime();
        if (!Number.isNaN(ts)) return ts;
    }
    return Number(enr?.id) || 0;
}

const AlunoTabFinanceiro = ({ registro }: iAlunoTabFinanceiro) => {
    const eils: iEIL[] = (registro?.enrollmentInLevel as iEIL[]) || [];
    const hasAnyEnrollment = eils.some((eil: any) => Array.isArray(eil?.enrollment) && eil.enrollment.length > 0);

    const mostRecentEnrollmentId = React.useMemo(() => {
        let bestId: number | null = null;
        let bestScore = -Infinity;
        eils.forEach((eil: any) => {
            (eil?.enrollment ?? []).forEach((enr: any) => {
                const score = enrollmentRecency(enr);
                if (enr?.id != null && score > bestScore) {
                    bestScore = score;
                    bestId = enr.id;
                }
            });
        });
        return bestId;
    }, [eils]);

    const [expandedId, setExpandedId] = React.useState<number | null>(null);
    React.useEffect(() => {
        setExpandedId(mostRecentEnrollmentId);
    }, [mostRecentEnrollmentId]);

    const handleToggle = (enrollmentId: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedId(isExpanded ? enrollmentId : null);
    };

    if (!hasAnyEnrollment) {
        return (
            <Paper sx={{ p: 2 }}>
                <Typography variant="body2">Nenhuma matricula encontrada para este aluno.</Typography>
            </Paper>
        );
    }

    return (
        <Grid container spacing={2}>
            {eils.map((eil: any, eilIx: number) => (
                <Grid item xs={12} key={`eil-${eil.id ?? eilIx}`}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                                {eil?.level?.Description ?? "Nivel"}
                            </Typography>
                            <Chip label={`EIL #${eil?.id ?? ""}`} size="small" variant="outlined" />
                        </Box>

                        {(eil?.enrollment ?? []).map((enr: any, enrIx: number) => {
                            const installments = sortInstallments(enr?.installments ?? []);
                            const totalAmount = installments.reduce((acc: number, p: any) => acc + (Number(p?.Amount) || 0), 0);
                            const totalPaid = installments.reduce((acc: number, p: any) => acc + (Number(p?.TotalPayment) || 0), 0);
                            const enrollmentId: number | null = enr?.id ?? null;
                            const isExpanded = enrollmentId != null && expandedId === enrollmentId;

                            return (
                                <Accordion
                                    key={`enr-${enrollmentId ?? enrIx}`}
                                    expanded={isExpanded}
                                    onChange={enrollmentId != null ? handleToggle(enrollmentId) : undefined}
                                    sx={{ mb: 1, backgroundColor: "background.default" }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                                            <Typography variant="subtitle2">
                                                Matricula #{enrollmentId ?? "-"}
                                            </Typography>
                                            {enr?.PaymentCard && (
                                                <Chip label={`Cartao ${enr.PaymentCard}`} size="small" variant="outlined" />
                                            )}
                                            {enr?.Date && (
                                                <Chip label={`Inicio ${formatDateBR(enr.Date)}`} size="small" variant="outlined" />
                                            )}
                                            <Chip label={`${installments.length} parcela(s)`} size="small" />
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Typography variant="caption" color="text.secondary">
                                                Total: {formatBRL(totalAmount)} | Pago: {formatBRL(totalPaid)}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0 }}>
                                        {installments.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                                Sem parcelas cadastradas para esta matricula.
                                            </Typography>
                                        ) : (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                                                            <TableCell sx={{ fontWeight: "bold" }}>Vencimento</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: "bold" }}>Valor</TableCell>
                                                            <TableCell sx={{ fontWeight: "bold" }}>Data Pagamento</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: "bold" }}>Valor Pago</TableCell>
                                                            <TableCell sx={{ fontWeight: "bold" }}>id Asaas</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {installments.map((p: any) => (
                                                            <TableRow key={p?.id} hover>
                                                                <TableCell>{p?.NumberInEnrollment ?? "-"}</TableCell>
                                                                <TableCell>{formatDateBR(p?.DueDate)}</TableCell>
                                                                <TableCell align="right">{formatBRL(p?.Amount)}</TableCell>
                                                                <TableCell>{formatDateBR(p?.PayDate)}</TableCell>
                                                                <TableCell align="right">{formatBRL(p?.TotalPayment)}</TableCell>
                                                                <TableCell>
                                                                    {p?.idAsaas ? (
                                                                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                                                                            {p.idAsaas}
                                                                        </Typography>
                                                                    ) : "-"}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

export default AlunoTabFinanceiro;
